import { useEffect, useState } from "react";
import { Download, Check, X as XIcon, AlertTriangle } from "lucide-react";
import { useBranding } from "../context/TenantBrandingContext";

/**
 * One-click PWA install button.
 *
 * The PWA install prompt is captured as early as possible by an inline
 * script in `public/index.html` and stashed on `window.__hvDeferredInstall`.
 * That guarantees we never miss the `beforeinstallprompt` event, even if
 * the user lands on a slow first paint.
 *
 * Behaviour:
 *   - If a deferred prompt is available → click triggers the native
 *     Chrome/Android/Edge install dialog directly.
 *   - On iOS Safari (no event ever fires) → click reveals the Share-sheet
 *     instructions inline.
 *   - On Android Chrome when the event hasn't fired → click reveals a
 *     diagnostic checklist so you can see WHY Chrome decided the site
 *     isn't installable (no more silent failures, no more guessing).
 *   - On already-installed PWAs → button hides itself.
 *   - Permanent: re-renders if the event fires later.
 *
 * Variants:
 *   "primary"  — full button (Settings, Login)
 *   "ghost"    — subtle inline link
 */

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

function isiOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

export default function InstallPWAButton({ variant = "primary", className = "" }) {
  const { branding } = useBranding();
  const brandName = branding?.app_name || "Rogue";
  const apkSlug = brandName.replace(/[^A-Za-z0-9]+/g, "");
  const [available, setAvailable] = useState(
    typeof window !== "undefined" && !!window.__hvDeferredInstall,
  );
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onAvailable = () => setAvailable(!!window.__hvDeferredInstall);
    const onInstalled = () => {
      setAvailable(false);
      setInstalled(true);
    };
    window.addEventListener("hv:install-available", onAvailable);
    window.addEventListener("hv:install-installed", onInstalled);
    // Some browsers fire `appinstalled` only once on the original window
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("hv:install-available", onAvailable);
      window.removeEventListener("hv:install-installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const ev = window.__hvDeferredInstall;
    if (ev) {
      try {
        ev.prompt();
        const choice = await ev.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
      } catch (_e) {
        /* ignore */
      } finally {
        window.__hvDeferredInstall = null;
        setAvailable(false);
      }
      return;
    }
    // No deferred prompt — iOS or Android where Chrome decided the site
    // isn't installable. On Android we ALSO offer a direct APK sideload
    // (Wave 19 #81): tapping the button kicks off the .apk download
    // immediately — exact behaviour the user asked for. The help modal
    // still opens (so the user sees install steps + alt browsers) but
    // the download fires first so the file lands in their browser
    // without an extra tap.
    if (isAndroid()) {
      try {
        // Use a hidden anchor so the download attribute is honoured by Chrome.
        const a = document.createElement("a");
        a.href = "/api/install/apk";
        a.download = `${apkSlug}.apk`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_e) { /* ignore — fall through to help modal */ }
    }
    setShowHelp(true);
  };

  if (variant === "ghost") {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          className={`text-xs text-[#1fb6c1] hover:text-white inline-flex items-center gap-1 ${className}`}
          data-testid="install-pwa-btn-ghost"
          title={`Install ${brandName}`}
        >
          <Download className="w-3 h-3" /> Install app
        </button>
        {showHelp && <InstallHelp onClose={() => setShowHelp(false)} brandName={brandName} apkSlug={apkSlug} />}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`hv-btn hv-btn-primary ${className}`}
        data-testid="install-pwa-btn"
        title={`Install ${brandName}`}
      >
        <Download className="w-4 h-4" /> Install app
      </button>
      {showHelp && <InstallHelp onClose={() => setShowHelp(false)} brandName={brandName} apkSlug={apkSlug} />}
    </>
  );
}

/* ─── Install help / diagnostic ─────────────────────────────── */
function InstallHelp({ onClose, brandName = "Rogue", apkSlug = "Rogue" }) {
  const ios = isiOS();
  const android = isAndroid();

  if (ios) {
    return (
      <HelpShell onClose={onClose} title="Install on iPhone / iPad">
        <ol className="text-xs text-[#a0aabf] space-y-2 list-decimal pl-5 mb-3">
          <li>Tap the <span className="font-bold text-white">Share</span> button at the bottom of Safari.</li>
          <li>Scroll and tap <span className="font-bold text-white">Add to Home Screen</span>.</li>
          <li>Hit <span className="font-bold text-white">Add</span> — done.</li>
        </ol>
      </HelpShell>
    );
  }

  if (android) {
    return (
      <HelpShell onClose={onClose} title="Install on Android">
        <div className="text-xs text-[#a0aabf] mb-3">
          Chrome hasn't offered the install prompt yet. Here's why — and how to fix it:
        </div>
        <PWADiagnostic />
        <div className="mt-3 text-xs text-[#a0aabf]">
          If all checks pass, open Chrome's menu (<span className="font-mono">⋮</span>) → <span className="font-bold text-white">Install app</span> or <span className="font-bold text-white">Add to Home screen</span>. If you don't see that option, pull down to refresh the page once, wait 30 seconds, and try again.
        </div>
        <AndroidStubbornBypass brandName={brandName} apkSlug={apkSlug} />
      </HelpShell>
    );
  }

  return (
    <HelpShell onClose={onClose} title="Install via browser menu">
      <ol className="text-xs text-[#a0aabf] space-y-2 list-decimal pl-5 mb-3">
        <li>Open your browser's menu (three dots <span className="font-mono">⋮</span>).</li>
        <li>Tap <span className="font-bold text-white">Install app</span> or <span className="font-bold text-white">Add to Home screen</span>.</li>
        <li>Confirm — {brandName} opens in its own window.</li>
      </ol>
    </HelpShell>
  );
}

function HelpShell({ onClose, title, children }) {
  return (
    <div
      className="fixed inset-0 z-[1100] flex items-end md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="install-help-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="hv-card p-5 max-w-sm w-full">
        <div className="font-display text-base font-bold mb-2">{title}</div>
        {children}
        <button
          type="button"
          onClick={onClose}
          className="hv-btn hv-btn-ghost text-xs w-full justify-center"
          data-testid="install-help-close"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/* ─── PWA installability diagnostic (Android Chrome)
 *
 * Runs live checks against the rules at
 * https://web.dev/articles/install-criteria and shows pass/fail so the
 * rep can see exactly why Chrome refuses to install. Removes the "must
 * be my device settings" guesswork.
 * ────────────────────────────────────────────────────────────────── */
function PWADiagnostic() {
  const [checks, setChecks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await runPwaChecks();
      if (!cancelled) setChecks(result);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!checks) {
    return (
      <div className="text-xs text-[#647087] py-2" data-testid="pwa-diag-loading">
        Running installability checks…
      </div>
    );
  }

  return (
    <ul className="space-y-1" data-testid="pwa-diag">
      {checks.map((c, i) => (
        <li
          key={i}
          className="flex items-start gap-2 text-xs"
          data-testid={`pwa-diag-${c.key}`}
        >
          {c.status === "pass" ? (
            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
          ) : c.status === "warn" ? (
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          ) : (
            <XIcon className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className={`${c.status === "fail" ? "text-rose-300" : c.status === "warn" ? "text-amber-300" : "text-[#dde3ec]"}`}>
              {c.label}
            </div>
            {c.note && (
              <div className="text-[10px] text-[#647087] mt-0.5 break-words">{c.note}</div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

async function runPwaChecks() {
  const out = [];
  const add = (key, label, status, note) => out.push({ key, label, status, note });

  // 1. HTTPS
  const secure = window.location.protocol === "https:" || window.location.hostname === "localhost";
  add("https", "Served over HTTPS", secure ? "pass" : "fail", secure ? null : `Current protocol: ${window.location.protocol}`);

  // 2. Manifest
  let manifest = null;
  try {
    const linkEl = document.querySelector('link[rel="manifest"]');
    if (!linkEl) {
      add("manifest-link", "Manifest linked in HTML", "fail", "No <link rel=\"manifest\"> tag");
    } else {
      add("manifest-link", "Manifest linked in HTML", "pass", linkEl.href);
      const res = await fetch(linkEl.href, { credentials: "omit" });
      if (!res.ok) {
        add("manifest-fetch", "Manifest fetches OK", "fail", `HTTP ${res.status}`);
      } else {
        manifest = await res.json();
        add("manifest-fetch", "Manifest fetches OK", "pass");
      }
    }
  } catch (e) {
    add("manifest-fetch", "Manifest fetches OK", "fail", String(e.message || e));
  }

  if (manifest) {
    add(
      "manifest-name",
      "Manifest has name or short_name",
      manifest.name || manifest.short_name ? "pass" : "fail",
    );
    add(
      "manifest-start",
      "Manifest has start_url",
      manifest.start_url ? "pass" : "fail",
    );
    const goodDisplay = ["standalone", "fullscreen", "minimal-ui"].includes(manifest.display);
    add(
      "manifest-display",
      "Manifest display = standalone/fullscreen/minimal-ui",
      goodDisplay ? "pass" : "fail",
      goodDisplay ? null : `Got: ${manifest.display || "(none)"}`,
    );
    // Icons — need at least one >=192 PNG with purpose any (or no purpose)
    const icons = manifest.icons || [];
    const has192 = icons.some((i) => {
      const sizes = String(i.sizes || "").split(/\s+/);
      const hasBig = sizes.some((s) => {
        const [w] = s.split("x").map((n) => Number(n));
        return w >= 192;
      });
      const anyPurpose = !i.purpose || i.purpose.split(/\s+/).includes("any");
      return hasBig && anyPurpose;
    });
    add(
      "manifest-icon-192",
      "Manifest has ≥192px icon with purpose \"any\"",
      has192 ? "pass" : "fail",
    );
    const has512 = icons.some((i) => {
      const sizes = String(i.sizes || "").split(/\s+/);
      return sizes.some((s) => {
        const [w] = s.split("x").map((n) => Number(n));
        return w >= 512;
      });
    });
    add(
      "manifest-icon-512",
      "Manifest has ≥512px icon",
      has512 ? "pass" : "fail",
    );
    // Verify one 192 icon actually loads
    const probe = icons.find((i) => String(i.sizes || "").includes("192"));
    if (probe) {
      try {
        const r = await fetch(new URL(probe.src, window.location.origin).href, { credentials: "omit" });
        add(
          "manifest-icon-load",
          "192px icon loads",
          r.ok ? "pass" : "fail",
          r.ok ? null : `HTTP ${r.status}`,
        );
      } catch (e) {
        add("manifest-icon-load", "192px icon loads", "fail", String(e.message || e));
      }
    }
  }

  // 3. Service worker
  if (!("serviceWorker" in navigator)) {
    add("sw-support", "Browser supports service workers", "fail");
  } else {
    add("sw-support", "Browser supports service workers", "pass");
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      if (!reg) {
        add("sw-registered", "Service worker is registered", "fail", "No registration for scope /");
      } else {
        add(
          "sw-registered",
          "Service worker is registered",
          "pass",
          `scope: ${reg.scope}`,
        );
        const hasActive = !!reg.active;
        add(
          "sw-active",
          "Service worker is active",
          hasActive ? "pass" : "warn",
          hasActive ? null : "SW registered but not yet activated — reload the page once.",
        );
      }
    } catch (e) {
      add("sw-registered", "Service worker is registered", "fail", String(e.message || e));
    }
  }

  // 4. Already installed?
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  if (standalone) {
    add("already-installed", "Not already installed", "warn", "Currently running in standalone mode — app is already installed.");
  } else {
    add("already-installed", "Not already installed", "pass");
  }

  // 5. Engagement heuristic — can't truly check, but flag it
  add(
    "engagement",
    "User has engaged with the page",
    "warn",
    "Chrome waits ~30s + requires a tap/scroll before showing the install prompt. If all checks above pass, scroll + wait, then check the browser menu.",
  );

  return out;
}


/* ─── Android stubborn-device bypass ─────────────────────────────────
 *
 * Some Android devices (work-managed Chrome, Android Go, OEM-modified
 * Chromium like Huawei/HONOR, certain Samsung/Realme builds, Chrome's
 * own A/B-test populations) suppress the `beforeinstallprompt` event
 * entirely — every diagnostic check passes but the install button never
 * appears. For these users we offer three escape hatches.
 * ────────────────────────────────────────────────────────────────── */
function AndroidStubbornBypass({ brandName = "Rogue", apkSlug = "Rogue" }) {
  const url = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/+$/, "");
  const intent = (pkg, scheme = "https") =>
    `intent://${url.replace(/^https?:\/\//, "")}#Intent;scheme=${scheme};package=${pkg};end`;
  const browsers = [
    { name: "Microsoft Edge", pkg: "com.microsoft.emmx", testid: "bypass-edge" },
    { name: "Samsung Internet", pkg: "com.sec.android.app.sbrowser", testid: "bypass-samsung" },
    { name: "Brave", pkg: "com.brave.browser", testid: "bypass-brave" },
    { name: "Firefox", pkg: "org.mozilla.firefox", testid: "bypass-firefox" },
  ];
  return (
    <div className="mt-4 pt-3 border-t border-white/10" data-testid="install-stubborn-bypass">
      <div className="text-[11px] font-bold text-amber-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" />
        Still no Install button?
      </div>
      <div className="text-[11px] text-[#a0aabf] mb-2">
        Some Android devices (work-managed phones, Huawei, Realme, certain
        Samsung builds) refuse Chrome's install prompt. Try one of these:
      </div>
      <div className="text-[11px] text-[#dde3ec] mb-1.5">
        <strong className="text-white">Method 1.</strong> Open this site in another browser — they each have their OWN install rules:
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3" data-testid="bypass-browsers">
        {browsers.map((b) => (
          <a
            key={b.pkg}
            href={intent(b.pkg)}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] px-2 py-1.5 rounded border border-[#1fb6c1]/30 bg-[#1fb6c1]/10 text-[#1fb6c1] hover:bg-[#1fb6c1]/20 text-center"
            data-testid={b.testid}
          >
            Open in {b.name}
          </a>
        ))}
      </div>
      <div className="text-[10px] text-[#647087] mb-3 -mt-1">
        After it opens, look for the install / "Add page to" option in that browser's menu.
        If a browser isn't installed, the link will silently fail — try the next one.
      </div>
      <div className="text-[11px] text-[#dde3ec] mb-1.5">
        <strong className="text-white">Method 2.</strong> Download the official Android APK directly (no Play Store, no PWABuilder, just one tap):
      </div>
      <a
        href="/api/install/apk"
        download={`${apkSlug}.apk`}
        className="block text-[11px] px-3 py-2 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-center mb-3"
        data-testid="bypass-apk-direct"
      >
        Download {brandName} APK (1.3 MB)
      </a>
      <div className="text-[10px] text-[#647087] mb-3 -mt-1">
        After download: tap the .apk in your browser's downloads / file manager. If
        Android asks "Install unknown apps", allow it for that app. {brandName}
        appears as a real Android icon — same login as the website.
      </div>
      <div className="text-[11px] text-[#dde3ec] mb-1.5">
        <strong className="text-white">Method 3.</strong> Reset Chrome's cache for this site (last-ditch):
      </div>
      <ol className="text-[10px] text-[#a0aabf] space-y-1 list-decimal pl-5">
        <li>In Chrome, tap <span className="font-mono">⋮</span> → <strong>Settings</strong> → <strong>Site settings</strong> → <strong>All sites</strong>.</li>
        <li>Find <span className="font-mono">{url.replace(/^https?:\/\//, "")}</span> → <strong>Clear &amp; reset</strong>.</li>
        <li>Reload this page, wait 30 seconds, scroll, then check the menu again.</li>
      </ol>
    </div>
  );
}
